import { useEffect } from "react";

const UNREAD_PREFIX = /^\(\d+\+?\) /;

export const withUnreadCount = (title: string, unreadCount: number) => {
  const base = title.replace(UNREAD_PREFIX, "");
  if (unreadCount <= 0) return base;
  return `(${unreadCount > 99 ? "99+" : unreadCount}) ${base}`;
};

/**
 * Shows unread chat messages in the browser tab, "(2) Billing | Suno - …", so a
 * glance at the tab bar says something is waiting after a notification is gone.
 *
 * Pages set `document.title` themselves (`useSetDocumentTitle`), in their own
 * effects and at any time, so the count is re-applied whenever the title
 * element changes rather than once per render.
 */
export default function useUnreadChatTitle(unreadCount: number) {
  useEffect(() => {
    const apply = () => {
      const next = withUnreadCount(document.title, unreadCount);
      // Only write a change: writing the same title would re-trigger the observer.
      if (document.title !== next) document.title = next;
    };
    apply();
    const titleElement = document.querySelector("title");
    if (!titleElement || typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(apply);
    observer.observe(titleElement, {
      childList: true,
      characterData: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      const base = withUnreadCount(document.title, 0);
      if (document.title !== base) document.title = base;
    };
  }, [unreadCount]);
}
